import { DatabaseBackup } from "lucide-react";
import { ago } from "../../lib/dash";
import { Tile, Chip, Empty, TileLink } from "./Tile";

const DOT = { red: "bg-rose-500", amber: "bg-amber-400" };

const mailText = (a, rule) => {
  if (rule?.email_locked) return rule.note;
  if (rule && !rule.email) return "email off for this rule";
  if (a.mail_note) return a.mail_note;
  return a.mailed_at ? `emailed ${ago(a.mailed_at)}` : "email pending (at most one per rule per hour)";
};

// A same-page tab link has to be a plain anchor so the hash change switches the tab.
const Go = ({ to, testid }) => (to.startsWith("/admin#")
  ? <a href={to} data-testid={testid} className="shrink-0 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-accent-strong transition-colors hover:bg-accent hover:text-accent-foreground">Open</a>
  : <TileLink to={to} testid={testid}>Open</TileLink>);

const AlertRow = ({ a, rule, id, done }) => (
  <li data-testid={id} className="flex items-start gap-2.5 border-b border-border/60 py-2 last:border-0">
    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${done ? "bg-emerald-500" : DOT[a.severity] || "bg-muted-foreground"}`} />
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2">
        <p data-testid={`${id}-title`} className="truncate text-[13px] font-semibold">{a.title}</p>
        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{done ? `fixed ${ago(a.resolved_at)}` : `since ${ago(a.opened_at)}`}</span>
      </div>
      {a.detail && <p className="truncate text-[12px] text-muted-foreground">{a.detail}</p>}
      <p data-testid={`${id}-mail`} className="truncate text-[11px] text-muted-foreground">{a.label} · {done ? "closed by the next run" : mailText(a, rule)}</p>
    </div>
    {a.link && !done && <Go to={a.link} testid={`${id}-link`} />}
  </li>
);

export const BackupRow = ({ b }) => (
  <div data-testid="alerts-backup" data-tone={b.tone} className={`rounded-xl border px-3 py-2 ${b.tone === "red" ? "border-rose-300 bg-rose-50 text-rose-800" : "border-amber-300 bg-amber-50/70 text-amber-900"}`}>
    <p className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.16em]"><DatabaseBackup className="h-3.5 w-3.5" /> Backups</p>
    <p data-testid="alerts-backup-text" className="mt-0.5 text-[13px] font-semibold leading-snug">{b.text}</p>
    {b.note && <p data-testid="alerts-backup-note" className="text-[11px] opacity-80">{b.note}</p>}
    {b.here && <p data-testid="alerts-backup-here" className="text-[11px] opacity-80">{b.here}</p>}
  </div>
);

const Heading = ({ children }) => <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground first:mt-0">{children}</p>;

export const AlertsTile = ({ d, index }) => {
  const rules = Object.fromEntries(d.rules.map((r) => [r.id, r]));
  return (
    <Tile id="alerts" index={index} tone={d.worst} kicker={`Tile 9 · rules run every ${d.every_minutes} min`} title="Alerts"
      action={<Chip testid="alerts-open-count" tone={d.worst}>{d.active.length ? `${d.active.length} open` : "All clear"}</Chip>}>
      <BackupRow b={d.backup} />
      <div className="dash-scroll mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto pr-1">
        <Heading>Open now</Heading>
        {d.active.length ? (
          <ul data-testid="alerts-open">{d.active.map((a, i) => <AlertRow key={`${a.rule}|${a.subject}`} a={a} rule={rules[a.rule]} id={`alert-open-${i}`} />)}</ul>
        ) : (
          <Empty testid="alerts-none">Nothing open. New problems email the owner, at most once per rule per hour.</Empty>
        )}
        <Heading>Fixed in the last 7 days</Heading>
        {d.recent.length ? (
          <ul data-testid="alerts-recent">{d.recent.map((a, i) => <AlertRow key={`${a.rule}|${a.subject}`} a={a} id={`alert-recent-${i}`} done />)}</ul>
        ) : (
          <p data-testid="alerts-recent-none" className="py-1 text-[12px] text-muted-foreground">Nothing fixed recently.</p>
        )}
      </div>
    </Tile>
  );
};
